import React, { useMemo, useState } from 'react';
import { useBillingSummary, useTransactions } from '@/hooks/api/useBilling';
import { LoadingSpinner, ErrorMessage } from '@/components/ui';

interface UsageStatisticsProps {
  className?: string;
}

export const UsageStatistics: React.FC<UsageStatisticsProps> = ({ className = '' }) => {
  // Default to current month
  const [dateRange] = useState(() => {
    const now = new Date();
    const startDate = new Date(now.getFullYear(), now.getMonth(), 1);
    const endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    return {
      start: startDate.toISOString().split('T')[0],
      end: endDate.toISOString().split('T')[0],
    };
  });

  const {
    data: summary,
    isLoading: summaryLoading,
    error: summaryError,
    refetch: refetchSummary,
  } = useBillingSummary(dateRange.start, dateRange.end);

  const {
    data: transactions,
    isLoading: transactionsLoading,
    error: transactionsError,
  } = useTransactions({ page: 1, page_size: 100 });

  const stats = useMemo(() => {
    const totalSpent = Number(summary?.total_spent ?? 0);
    const monthlySearches = summary?.total_searches ?? 0;
    const monthlyCalculations = summary?.total_calculations ?? 0;

    // Calculate totals from all transactions (defensive against undefined)
    // Debits represent searches/calculations (credits spent)
    const totalSearches =
      transactions?.items?.filter((t) => t.transaction_type === 'debit').length ?? 0;

    const totalCalculations = 0; // Calculations are included in debit transactions

    return {
      totalSpent,
      monthlySearches,
      monthlyCalculations,
      totalSearches,
      totalCalculations,
    };
  }, [summary, transactions]);

  const isLoading = summaryLoading || transactionsLoading;
  const error = summaryError || transactionsError;

  if (isLoading) {
    return (
      <div className={`bg-white rounded-lg shadow-md p-6 ${className}`}>
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Usage Statistics</h2>
        <div className="flex justify-center py-8">
          <LoadingSpinner size="md" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`bg-white rounded-lg shadow-md p-6 ${className}`}>
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Usage Statistics</h2>
        <ErrorMessage
          message="Failed to load usage statistics"
          onRetry={() => refetchSummary()}
        />
      </div>
    );
  }

  return (
    <div className={`bg-white rounded-lg shadow-md p-6 ${className}`}>
      <h2 className="text-xl font-semibold text-gray-900 mb-4">Usage Statistics</h2>

      <div className="space-y-4">
        {/* This Month Section */}
        <div className="pb-4 border-b border-gray-200">
          <h3 className="text-sm font-medium text-gray-500 mb-3">This Month</h3>
          <div className="space-y-3">
            <div>
              <p className="text-sm text-gray-600">Searches</p>
              <p className="text-2xl font-bold text-gray-900">{stats.monthlySearches}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Calculations</p>
              <p className="text-2xl font-bold text-gray-900">{stats.monthlyCalculations}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Credits Used</p>
              <p className="text-2xl font-bold text-gray-900">{stats.totalSpent.toFixed(1)} credits</p>
            </div>
          </div>
        </div>

        {/* All Time Section */}
        <div>
          <h3 className="text-sm font-medium text-gray-500 mb-3">All Time</h3>
          <div className="space-y-3">
            <div>
              <p className="text-sm text-gray-600">Total Searches</p>
              <p className="text-2xl font-bold text-gray-900">{stats.totalSearches}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Total Calculations</p>
              <p className="text-2xl font-bold text-gray-900">{stats.totalCalculations}</p>
            </div>
          </div>
        </div>

        {/* Transaction Type Breakdown */}
        {summary?.transactions_by_type && (
          <div className="pt-4 border-t border-gray-200">
            <h3 className="text-sm font-medium text-gray-500 mb-3">Activity Breakdown</h3>
            <div className="space-y-2">
              {Object.entries(summary.transactions_by_type).map(([type, count]) => (
                <div key={type} className="flex justify-between items-center">
                  <span className="text-sm text-gray-600 capitalize">
                    {type.replace('_', ' ')}
                  </span>
                  <span className="text-sm font-medium text-gray-900">{count}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
